import { z } from "zod";
import { RegionDtoSchema, regionFromDto, RegionSchema } from "./region";
import {
  numToReservationRegionEnum,
  ReservationRegion,
  reservationRegionEnumToNumber,
} from "./enums";
import { apiListSchema } from "./list";

//? DTO
export const CreateTourDtoSchema = z.object({
  ChainIndex: z.number().nullish(),
  Name: z.string(),
  Category: z.number().nullish(),
  Exclusivity: z.boolean().nullish(),
  Region: z.number().nullish(),
  Type: z.number().nullish(),
  TourLeaderId: z.number().nullish(),
  PercentageMap: z.string().nullish(),
  Details: z.string().nullish(),
  TourChainId: z.string().nullish(),
  Active: z.boolean().nullish(),
  FixedPrice: z.number().nullish(),
  SeasonalSupplementMap: z.string().nullish(),
  IndividualRoomSupplementMap: z.string().nullish(),
  SoloTravelerSupplement: z.number().nullish(),
  OtherOperationRegion: z.number().nullish(),
});

export type CreateTourDto = z.infer<typeof CreateTourDtoSchema>;

export const TourDtoSchema = CreateTourDtoSchema.extend({
  Id: z.number(),
  RegionNavigation: RegionDtoSchema.nullish(),
});

export type TourDto = z.infer<typeof TourDtoSchema>;

export const tourDtoListSchema = apiListSchema(TourDtoSchema);

//? Model

export enum TourCategory {
  Hike = "Hike",
  Mix = "Mix",
  BikeHybrid = "BikeHybrid",
  BikeMountain = "BikeMountain",
  BikeRoad = "BikeRoad",
  BikeGravel = "BikeGravel",
}

export const tourCategoryEnumToNumber: Record<TourCategory, number> = {
  Hike: 0,
  Mix: 1,
  BikeHybrid: 2,
  BikeMountain: 3,
  BikeRoad: 4,
  BikeGravel: 5,
};

const numToEnum = Object.entries(tourCategoryEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
) as Record<number, TourCategory>;

export const tourCategoriesAsArray = Object.values(TourCategory);

export enum TourType {
  Guided = "Guided",
  SelfGuided = "SelfGuided",
  Supported = "Supported",
  Mix = "Mix",
  Services = "Services",
  Internal = "Internal",
}

export const TourTypeInitials: Record<TourType, string> = {
  Guided: "GUIDED",
  SelfGuided: "SG",
  Supported: "SUPP",
  Mix: "MIX",
  Services: "SERV",
  Internal: "INT",
};

export const tourTypeInitials: Record<TourType, string> = {
  Guided: "G",
  SelfGuided: "SG",
  Supported: "Supported",
  Mix: "Mix",
  Services: "Services",
  Internal: "Internal",
};

export const tourTypeEnumToNumber: Record<TourType, number> = {
  Guided: 0,
  SelfGuided: 1,
  Supported: 2,
  Mix: 3,
  Services: 4,
  Internal: 5,
};

const numToTourTypeEnum = Object.entries(tourTypeEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
) as Record<number, TourType>;

export const tourTypesAsArray = Object.values(TourType);

export const CreateTourSchema = z.object({
  chainIndex: z.number().nullish(),
  name: z.string().min(1),
  category: z.nativeEnum(TourCategory).nullish(),
  exclusivity: z.boolean().nullish(),
  region: z.number().nullish(),
  type: z.nativeEnum(TourType).nullish(),
  tourLeaderId: z.number().nullish(),
  percentageMap: z.record(z.any()).nullish(),
  details: z.string().nullish(),
  tourChainId: z.string().nullish(),
  active: z.boolean().nullish(),
  fixedPrice: z.number().nullish(),
  seasonalSupplementMap: z.record(z.any()).nullish(),
  individualRoomSupplementMap: z.record(z.any()).nullish(),
  soloTravelerSupplement: z.number().nullish(),
  operationRegion: z.nativeEnum(ReservationRegion).nullish(),
});

export const TourSchema = CreateTourSchema.extend({
  id: z.string().min(1),
  regionNavigation: RegionSchema.nullish(),
});

export type CreateTour = z.infer<typeof CreateTourSchema>;

export type Tour = z.infer<typeof TourSchema>;

export function tourFromDto(dto: TourDto): Tour {
  return {
    id: dto.Id.toString(),
    chainIndex: dto.ChainIndex,
    name: dto.Name,
    category:
      typeof dto.Category === "number" ? numToEnum[dto.Category] : undefined,
    exclusivity: dto.Exclusivity,
    region: dto.Region,
    type:
      typeof dto.Type === "number" ? numToTourTypeEnum[dto.Type] : undefined,
    tourLeaderId: dto.TourLeaderId,
    percentageMap: dto.PercentageMap
      ? JSON.parse(dto.PercentageMap)
      : undefined,
    details: dto.Details,
    tourChainId: dto.TourChainId,
    active: dto.Active,
    fixedPrice: dto.FixedPrice,
    seasonalSupplementMap: dto.SeasonalSupplementMap
      ? JSON.parse(dto.SeasonalSupplementMap)
      : undefined,
    individualRoomSupplementMap: dto.IndividualRoomSupplementMap
      ? JSON.parse(dto.IndividualRoomSupplementMap)
      : undefined,
    soloTravelerSupplement: dto.SoloTravelerSupplement,
    regionNavigation: dto.RegionNavigation
      ? regionFromDto(dto.RegionNavigation)
      : undefined,
    operationRegion:
      typeof dto.OtherOperationRegion === "number"
        ? numToReservationRegionEnum[dto.OtherOperationRegion]
        : undefined,
  };
}

export function tourToDto(dto: Tour): TourDto {
  return {
    Id: parseInt(dto.id),
    ...createTourToDto(dto),
    RegionNavigation: undefined,
  };
}

export function createTourToDto(dto: CreateTour): CreateTourDto {
  return {
    ChainIndex: dto.chainIndex,
    Name: dto.name,
    Category: dto.category ? tourCategoryEnumToNumber[dto.category] : undefined,
    Exclusivity: dto.exclusivity,
    Region: dto.region,
    Type: dto.type ? tourTypeEnumToNumber[dto.type] : undefined,
    TourLeaderId: dto.tourLeaderId,
    PercentageMap: dto.percentageMap
      ? JSON.stringify(dto.percentageMap)
      : undefined,
    Details: dto.details,
    TourChainId: dto.tourChainId,
    Active: dto.active,
    FixedPrice: dto.fixedPrice,
    SeasonalSupplementMap: dto.seasonalSupplementMap
      ? JSON.stringify(dto.seasonalSupplementMap)
      : undefined,
    IndividualRoomSupplementMap: dto.individualRoomSupplementMap
      ? JSON.stringify(dto.individualRoomSupplementMap)
      : undefined,
    SoloTravelerSupplement: dto.soloTravelerSupplement,
    OtherOperationRegion: dto.operationRegion
      ? reservationRegionEnumToNumber[dto.operationRegion]
      : undefined,
  };
}

export const defaultTourPercentagesValues = {
  "Tour Base Price": 20,
  "Season Supplement": 10,
  "Individual Room Supplement": 10,
  "Solo Traveler Supplement": 10,
  "Room Change": 10,
  "Hotel Change": 10,
  "Bike Rental": 0,
  "Equipment Rental": 0,
};
